import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Grid, 
  CircularProgress,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import bancoService, { Banco } from '../../services/bancoService';

interface BancoFormData {
  nroBanco: number;
  nombreBanco: string;
}

const defaultForm: BancoFormData = {
  nroBanco: 0,
  nombreBanco: '',
};

const BancoForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroBanco } = useParams();
  const [formData, setFormData] = useState<BancoFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadBanco = useCallback(async () => {
    if (!nroBanco) return;
    try {
      setLoading(true);
      const banco = await bancoService.getBanco(Number(nroBanco));
      setFormData({
        nroBanco: banco.nroBanco,
        nombreBanco: banco.nombreBanco || '',
      });
    } catch (error) {
      enqueueSnackbar('Error al cargar el banco', { variant: 'error' });
      navigate('/tesoreria/bancos');
    } finally {
      setLoading(false);
    }
  }, [nroBanco, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroBanco) loadBanco();
  }, [nroBanco, loadBanco]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nroBanco') {
      const parsed = value === '' ? 0 : Number(value);
      setFormData(prev => ({ ...prev, [name]: parsed }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const requiredMissing: string[] = [];
      if (!formData.nroBanco || Number(formData.nroBanco) <= 0) requiredMissing.push('nroBanco');
      if (!String(formData.nombreBanco || '').trim()) requiredMissing.push('nombreBanco');
      if (requiredMissing.length) {
        enqueueSnackbar(`Complete los campos obligatorios: ${requiredMissing.join(', ')}`, { variant: 'warning' });
        setSaving(false);
        return;
      }
      if (Number(formData.nroBanco) >= 1000) {
        enqueueSnackbar('El número de banco debe ser menor a 1000.', { variant: 'error' });
        setSaving(false);
        return;
      }

      const payload: Banco = {
        nroBanco: Number(formData.nroBanco),
        nombreBanco: String(formData.nombreBanco).trim(),
      };

      if (nroBanco) {
        await bancoService.updateBanco(Number(nroBanco), payload);
      } else {
        await bancoService.createBanco(payload);
      }

      enqueueSnackbar('Banco guardado exitosamente', { variant: 'success' });
      navigate('/tesoreria/bancos');
    } catch (error: any) {
      let mostrado = false;
      const data = error?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        let detalle = '';
        for (const campo in data.errors) {
          if (Array.isArray(data.errors[campo])) {
            const nombreCampo = campo.charAt(0).toLowerCase() + campo.slice(1);
            detalle += `${nombreCampo}: ${data.errors[campo].join('; ')}\n`;
          }
        }
        if (detalle) {
          enqueueSnackbar(`Error en los datos: ${detalle}`, { variant: 'error', autoHideDuration: 12000 });
          mostrado = true;
        }
      }
      const messageText = (data?.message || data || error?.message || '').toString();
      if (!mostrado && messageText) {
        if (
          messageText.includes('PRIMARY KEY') ||
          messageText.toLowerCase().includes('duplicate') ||
          messageText.includes('Violation of PRIMARY KEY constraint')
        ) {
          enqueueSnackbar(`El banco con código ${formData.nroBanco} ya existe.`, { variant: 'error' });
          mostrado = true;
        }
      }
      if (!mostrado) enqueueSnackbar('Error al guardar el banco', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            {nroBanco ? 'Editar Banco' : 'Nuevo Banco'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="nroBanco" 
                  label="N° Banco" 
                  type="number" 
                  value={formData.nroBanco} 
                  onChange={handleChange} 
                  fullWidth 
                  required 
                  disabled={!!nroBanco}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField 
                  name="nombreBanco" 
                  label="Nombre Banco" 
                  value={formData.nombreBanco} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/tesoreria/bancos')} 
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BancoForm;

